import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseAvailable } from '../services/supabaseClient';
import { SubscriptionTier } from '../types';

export interface UsageLimitsData {
  canTakeTest: boolean;
  testsTaken: number;
  testsLimit: number;
  remainingTests: number;
  // Evolutivo analysis limits
  canGenerateEvolutivoAnalysis: boolean;
  evolutivoAnalysesTaken: number;
  evolutivoAnalysesLimit: number;
  remainingEvolutivoAnalyses: number;
  subscriptionTier: SubscriptionTier;
}

export interface UsageLimits extends UsageLimitsData {
  loading: boolean;
  error: string | null;
  refresh: () => Promise<UsageLimitsData | null>;
}

/**
 * Hook to get current user's usage limits based on subscription tier and monthly usage
 *
 * Returns:
 * - canTakeTest: boolean - Whether user can take another test this month
 * - testsTaken: number - Tests taken this month
 * - testsLimit: number - Max tests allowed per month (0 = unlimited)
 * - remainingTests: number - Tests remaining this month
 * - subscriptionTier: 'free' | 'pro' | 'enterprise'
 * - loading: boolean - Whether data is being fetched
 * - error: string | null - Error message if any
 * - refresh: function - Function to manually refresh limits
 */
export function useUsageLimits(): UsageLimits {
  const { user } = useAuth();
  const [limits, setLimits] = useState<UsageLimits>({
    canTakeTest: true,
    testsTaken: 0,
    testsLimit: 5,
    remainingTests: 5,
    canGenerateEvolutivoAnalysis: false,
    evolutivoAnalysesTaken: 0,
    evolutivoAnalysesLimit: 0,
    remainingEvolutivoAnalyses: 0,
    subscriptionTier: 'free',
    loading: true,
    error: null,
    refresh: async () => null,
  });

  const fetchLimits = async (): Promise<UsageLimitsData | null> => {
    if (!user) {
      setLimits(prev => ({ ...prev, loading: false }));
      return null;
    }

    // If Supabase not available, return default free limits
    if (!isSupabaseAvailable()) {
      const defaultLimits: UsageLimitsData = {
        canTakeTest: true,
        testsTaken: 0,
        testsLimit: 5,
        remainingTests: 5,
        canGenerateEvolutivoAnalysis: false,
        evolutivoAnalysesTaken: 0,
        evolutivoAnalysesLimit: 0,
        remainingEvolutivoAnalyses: 0,
        subscriptionTier: 'free',
      };
      setLimits(prev => ({
        ...prev,
        ...defaultLimits,
        loading: false,
        error: null,
      }));
      return defaultLimits;
    }

    try {
      setLimits(prev => ({ ...prev, loading: true, error: null }));

      // 1. Get profile (tier + limits)
      const { data: profile, error: profileError } = await supabase!
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        // Profile might not exist yet (trigger pending), treat as free tier
        console.warn('Profile not found, assuming free tier:', profileError);
        const freeLimits: UsageLimitsData = {
          canTakeTest: true,
          testsTaken: 0,
          testsLimit: 5,
          remainingTests: 5,
          canGenerateEvolutivoAnalysis: false,
          evolutivoAnalysesTaken: 0,
          evolutivoAnalysesLimit: 0,
          remainingEvolutivoAnalyses: 0,
          subscriptionTier: 'free',
        };
        setLimits(prev => ({
          ...prev,
          ...freeLimits,
          loading: false,
          error: null,
        }));
        return freeLimits;
      }

      // 2. Get current month usage
      const currentMonth = new Date().toISOString().slice(0, 7); // 'YYYY-MM'
      const { data: usage } = await supabase!
        .from('usage_tracking')
        .select('*')
        .eq('user_id', user.id)
        .eq('month_year', currentMonth)
        .maybeSingle(); // Use maybeSingle to avoid error if no record exists

      // If no usage record yet, tests_taken = 0
      const testsTaken = usage?.tests_taken || 0;
      const evolutivoAnalysesTaken = usage?.evolutivo_analyses_generated || 0;
      const testsLimit = profile.tests_limit_per_month;

      // Evolutivo limits based on tier
      const tier = profile.subscription_tier as SubscriptionTier;
      const evolutivoAnalysesLimit = tier === 'free' ? 0 : tier === 'pro' ? 30 : Infinity;

      // For pro/enterprise: testsLimit = 0 (unlimited)
      // For free: testsLimit = 5
      const isPremium = tier === 'pro' || tier === 'enterprise';
      const remainingTests = isPremium ? 999 : Math.max(0, testsLimit - testsTaken);
      const canTakeTest = isPremium || remainingTests > 0;

      // Evolutivo analysis limits
      const remainingEvolutivoAnalyses = evolutivoAnalysesLimit === Infinity
        ? 999
        : Math.max(0, evolutivoAnalysesLimit - evolutivoAnalysesTaken);
      const canGenerateEvolutivoAnalysis = tier !== 'free' && (evolutivoAnalysesLimit === Infinity || remainingEvolutivoAnalyses > 0);

      const newLimits: UsageLimitsData = {
        canTakeTest,
        testsTaken,
        testsLimit: isPremium ? 0 : testsLimit, // 0 = unlimited
        remainingTests,
        canGenerateEvolutivoAnalysis,
        evolutivoAnalysesTaken,
        evolutivoAnalysesLimit: evolutivoAnalysesLimit === Infinity ? 0 : evolutivoAnalysesLimit,
        remainingEvolutivoAnalyses,
        subscriptionTier: tier,
      };

      setLimits(prev => ({
        ...prev,
        ...newLimits,
        loading: false,
        error: null,
      }));

      return newLimits;
    } catch (err) {
      console.error('Failed to fetch usage limits:', err);
      setLimits(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load usage limits',
      }));
      return null;
    }
  };

  useEffect(() => {
    fetchLimits();
  }, [user]);

  // Re-fetch periodically to handle month transitions in long-running sessions
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchLimits();
    }, 60 * 60 * 1000); // every hour
    return () => clearInterval(interval);
  }, [user]);

  // Add refresh function to returned object
  useEffect(() => {
    setLimits(prev => ({
      ...prev,
      refresh: fetchLimits,
    }));
  }, [user]);

  return limits;
}
